import React from 'react';
import dayjs from 'dayjs';

/** KPI figures for the client profile, derived from its stores and the audits in the selected period. */
export function useClientDetailAnalytics(stores, recentAudits) {
    return React.useMemo(() => {
        // Store metrics
        const totalStores = stores?.length || 0;
        const activeStores = stores?.filter((s) => s.active)?.length || 0;
        const inactiveStores = totalStores - activeStores;

        // Audit metrics
        const totalAudits = recentAudits?.length || 0;
        const completedAudits = recentAudits?.filter((a) => a.status === 'completed')?.length || totalAudits;

        // Get unique stores audited
        const uniqueStoresAudited = new Set(recentAudits?.map((a) => a.storeId?._id)).size;
        const coveragePercent = totalStores > 0 ? Math.round((uniqueStoresAudited / totalStores) * 100) : 0;

        // Average audit time
        const avgAuditTime = totalAudits > 0 ? Math.round(recentAudits.reduce((sum, audit) => sum + (audit.totalTime || 0), 0) / totalAudits) : 0;

        // Missing products count
        const missingProductsCount =
            recentAudits?.reduce((count, audit) => {
                const unavailable = audit.products?.filter((p) => !p.isAvailable)?.length || 0;
                return count + unavailable;
            }, 0) || 0;

        // Recent activity (last 7 days)
        const sevenDaysAgo = dayjs().subtract(7, 'days');
        const recentActivity = recentAudits?.filter((a) => dayjs(a.createdAt).isAfter(sevenDaysAgo))?.length || 0;

        // Coverage tone: ≥80 good, ≥50 attention, otherwise bad.
        const coverageTone = coveragePercent >= 80 ? 'success' : coveragePercent >= 50 ? 'warning' : 'danger';

        return {
            totalStores,
            activeStores,
            inactiveStores,
            totalAudits,
            completedAudits,
            uniqueStoresAudited,
            coveragePercent,
            coverageTone,
            avgAuditTime,
            missingProductsCount,
            recentActivity
        };
    }, [stores, recentAudits]);
}
